/**
 * Configuração pública Memed (somente URL de script).
 * Nunca usa API key/secret no frontend.
 *
 * Env Vite é injetada via `hydrateMemedPublicEnv` (main.tsx),
 * evitando `import.meta` neste módulo (compatível com Jest).
 */

const DEFAULT_SCRIPT_URL =
  'https://integrations.memed.com.br/modulos/plataforma.sinapse-prescricao/build/sinapse-prescricao.min.js';

const ALLOWED_SCRIPT_HOSTS = new Set([
  'integrations.memed.com.br',
  'partners.memed.com.br',
  'sandbox.memed.com.br',
  'memed.com.br',
]);

export const MEMED_SCRIPT_ELEMENT_ID = 'drhouse-memed-script';
export const MEMED_MODULE_NAME = 'plataforma.prescricao';

type MemedPublicEnv = {
  VITE_MEMED_ENABLED?: string;
  VITE_MEMED_SCRIPT_URL?: string;
  VITE_MEMED_CERTIFICATE_EXAM_ENABLED?: string;
};

let hydratedEnv: MemedPublicEnv = {};

/** Chamado no bootstrap da app com valores de `import.meta.env`. */
export function hydrateMemedPublicEnv(env: MemedPublicEnv): void {
  hydratedEnv = {
    VITE_MEMED_ENABLED: env.VITE_MEMED_ENABLED,
    VITE_MEMED_SCRIPT_URL: env.VITE_MEMED_SCRIPT_URL,
    VITE_MEMED_CERTIFICATE_EXAM_ENABLED: env.VITE_MEMED_CERTIFICATE_EXAM_ENABLED,
  };
}

function readPublicEnv(key: keyof MemedPublicEnv): string {
  const fromHydrated = hydratedEnv[key];
  const proc = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process;
  const fromProcess = proc?.env?.[key];
  return String(fromHydrated ?? fromProcess ?? '').trim();
}

/** Soft-gate opcional no front; backend continua como fonte da verdade. */
export function isMemedFrontendEnabled(): boolean {
  const flag = readPublicEnv('VITE_MEMED_ENABLED').toLowerCase();
  if (flag === 'false' || flag === '0' || flag === 'off' || flag === 'no') {
    return false;
  }
  return Boolean(getMemedScriptUrl());
}

/**
 * Soft-gate de atestado/exame. Default ON quando Memed está ON e a env não é false.
 * Autoridade real continua no backend (MEMED_CERTIFICATE_EXAM_ENABLED).
 */
export function isMemedCertificateExamFrontendEnabled(): boolean {
  if (!isMemedFrontendEnabled()) {
    return false;
  }
  const flag = readPublicEnv('VITE_MEMED_CERTIFICATE_EXAM_ENABLED').toLowerCase();
  if (flag === 'false' || flag === '0' || flag === 'off' || flag === 'no') {
    return false;
  }
  return true;
}

export function getMemedScriptUrl(): string {
  const configured = readPublicEnv('VITE_MEMED_SCRIPT_URL');
  const url = configured || DEFAULT_SCRIPT_URL;
  if (!isAllowedMemedScriptUrl(url)) {
    throw new Error('URL do script Memed inválida ou não permitida');
  }
  return url;
}

export function isAllowedMemedScriptUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') return false;
    return ALLOWED_SCRIPT_HOSTS.has(parsed.hostname);
  } catch {
    return false;
  }
}
